<template>
  <v-row>
    <v-col cols="12">
      <v-card class="bs-card" flat>
        <!-- HEADER BAND -->
        <div class="bs-header pl-base-bg">
          <div class="bs-header-icon">
            <v-icon color="#fff" size="22">mdi-file-chart-outline</v-icon>
          </div>
          <div>
            <div class="bs-title">Balance Sheet</div>
            <div class="bs-subtitle">Assets, Liabilities and Equity, as of a date</div>
          </div>
        </div>

        <div class="pa-6">
          <v-alert type="info" outlined dense border="left" class="bs-disclaimer">
            This Balance Sheet is prepared on an accrual basis from your
            double-entry ledger (Journal Entries), including Accounts
            Receivable/Payable. Inventory is not revalued against actual
            stock movement. Verify with your accountant before relying on
            it for statutory or lending purposes.
          </v-alert>

          <!-- FILTERS -->
          <div class="bs-filter-bar">
            <v-row dense align="center">
              <v-col cols="12" md="3">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  offset-y
                  max-width="290"
                  min-width="290"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedAsOf"
                      label="As of"
                      readonly
                      outlined
                      dense
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>

                  <v-date-picker
                    v-model="asOf"
                    @input="dateMenu = false"
                  />
                </v-menu>
              </v-col>

              <v-col cols="12" md="9" class="bs-actions">
                <v-btn class="bs-apply-btn" @click="loadBalanceSheet" :loading="loading">
                  Apply
                </v-btn>

                <v-btn
                  outlined
                  color="#1d6f42"
                  class="ml-2"
                  :disabled="!balanceSheet"
                  @click="downloadExcel"
                >
                  <v-icon left size="16" color="#1d6f42">mdi-file-excel-box</v-icon>
                  Excel
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <template v-if="balanceSheet">
            <v-row dense class="mb-6">
              <v-col cols="6" md="3">
                <v-card class="bs-kpi-card bs-kpi-card--value pa-4" flat>
                  <div class="bs-kpi-label">Total Assets</div>
                  <div class="bs-kpi-value">{{ formatINR(balanceSheet.totalAssets) }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card class="bs-kpi-card bs-kpi-card--tax pa-4" flat>
                  <div class="bs-kpi-label">Total Liabilities</div>
                  <div class="bs-kpi-value">{{ formatINR(balanceSheet.totalLiabilities) }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card class="bs-kpi-card bs-kpi-card--total pa-4" flat>
                  <div class="bs-kpi-label">Total Equity</div>
                  <div class="bs-kpi-value">{{ formatINR(balanceSheet.totalEquity) }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card
                  class="bs-kpi-card pa-4"
                  :class="balanceSheet.isBalanced ? 'bs-kpi-card--total' : 'bs-kpi-card--warn'"
                  flat
                >
                  <div class="bs-kpi-label">Status</div>
                  <div class="bs-kpi-value">
                    <v-icon color="#fff" size="18" class="mr-1">
                      {{ balanceSheet.isBalanced ? "mdi-check-circle" : "mdi-alert-circle" }}
                    </v-icon>
                    {{ balanceSheet.isBalanced ? "Balanced" : "Not Balanced" }}
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <v-alert
              v-if="balanceSheet.inventoryEstimate !== null"
              type="warning"
              outlined
              dense
              border="left"
              class="bs-disclaimer"
            >
              Estimated inventory value (not included above, not ledger-backed):
              {{ formatINR(balanceSheet.inventoryEstimate) }} — computed from tracked
              products' stock quantity x cost price.
            </v-alert>

            <v-row dense>
              <v-col cols="12" md="6">
                <div class="bs-section-title">Assets</div>
                <v-simple-table class="bs-table">
                  <tbody>
                    <tr v-for="row in balanceSheet.assets" :key="row.accountId">
                      <td>{{ row.code }} {{ row.name }}</td>
                      <td class="text-right">{{ formatINR(row.balance) }}</td>
                    </tr>
                    <tr class="bs-total-row">
                      <td>Total Assets</td>
                      <td class="text-right">{{ formatINR(balanceSheet.totalAssets) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>

              <v-col cols="12" md="6">
                <div class="bs-section-title">Liabilities</div>
                <v-simple-table class="bs-table mb-4">
                  <tbody>
                    <tr v-for="row in balanceSheet.liabilities" :key="row.accountId">
                      <td>{{ row.code }} {{ row.name }}</td>
                      <td class="text-right">{{ formatINR(row.balance) }}</td>
                    </tr>
                    <tr v-if="!balanceSheet.liabilities.length">
                      <td colspan="2" class="bs-empty-cell">No liabilities</td>
                    </tr>
                    <tr class="bs-total-row">
                      <td>Total Liabilities</td>
                      <td class="text-right">{{ formatINR(balanceSheet.totalLiabilities) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>

                <div class="bs-section-title">Equity</div>
                <v-simple-table class="bs-table">
                  <tbody>
                    <tr v-for="(row, i) in balanceSheet.equity" :key="row.accountId || `equity-${i}`">
                      <td>{{ row.code }} {{ row.name }}</td>
                      <td class="text-right">{{ formatINR(row.balance) }}</td>
                    </tr>
                    <tr class="bs-total-row">
                      <td>Total Equity</td>
                      <td class="text-right">{{ formatINR(balanceSheet.totalEquity) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
          </template>

          <div v-else class="bs-empty-state">
            Select a date and click Apply to generate the Balance Sheet.
          </div>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { downloadMultiSheetWorkbook } from "@/utils/xlsx-export";

export default {
  name: "BalanceSheetPage",

  data() {
    return {
      loading: false,
      dateMenu: false,
      asOf: new Date().toISOString().slice(0, 10),
    };
  },

  computed: {
    balanceSheet() {
      return this.$store.state.ledgerReports.balanceSheet;
    },

    formattedAsOf() {
      if (!this.asOf) return "";
      const d = new Date(this.asOf);
      return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    },
  },

  created() {
    this.loadBalanceSheet();
  },

  methods: {
    formatINR(value) {
      if (value === null || value === undefined) return "0";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value);
    },

    async loadBalanceSheet() {
      this.loading = true;
      try {
        await this.$store.dispatch("ledgerReports/getBalanceSheet", { asOf: this.asOf });
      } catch (err) {
        console.error(err);
        this.$toast?.error(
          err?.response?.data?.message || "Failed to load Balance Sheet",
        );
      } finally {
        this.loading = false;
      }
    },

    downloadExcel() {
      const b = this.balanceSheet;

      const assetRows = b.assets.map((r) => [`${r.code} ${r.name}`, Number(r.balance) || 0]);
      assetRows.push(["Total Assets", Number(b.totalAssets) || 0]);

      const liabilityRows = b.liabilities.map((r) => [`${r.code} ${r.name}`, Number(r.balance) || 0]);
      liabilityRows.push(["Total Liabilities", Number(b.totalLiabilities) || 0]);

      const equityRows = b.equity.map((r) => [`${r.code} ${r.name}`, Number(r.balance) || 0]);
      equityRows.push(["Total Equity", Number(b.totalEquity) || 0]);

      downloadMultiSheetWorkbook(
        [
          {
            name: "Assets",
            title: "Assets",
            subtitle: `As of ${this.formattedAsOf}`,
            headers: ["Account", "Balance"],
            rows: assetRows,
            columnWidths: [30, 18],
            numberColumns: [1],
          },
          {
            name: "Liabilities",
            title: "Liabilities",
            subtitle: `As of ${this.formattedAsOf}`,
            headers: ["Account", "Balance"],
            rows: liabilityRows,
            columnWidths: [30, 18],
            numberColumns: [1],
          },
          {
            name: "Equity",
            title: "Equity",
            subtitle: `As of ${this.formattedAsOf}`,
            headers: ["Account", "Balance"],
            rows: equityRows,
            columnWidths: [30, 18],
            numberColumns: [1],
          },
        ],
        `balance-sheet-${this.asOf}.xlsx`,
      );
    },
  },
};
</script>

<style scoped>
.bs-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.bs-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.bs-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.bs-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.bs-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.bs-disclaimer {
  margin-bottom: 20px;
  font-size: 13px;
}

.bs-filter-bar {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 16px 4px;
  margin-bottom: 24px;
}

.bs-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 0;
  padding-top: 4px;
}

.bs-apply-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.bs-kpi-card {
  border-radius: 0;
  color: #fff;
  box-shadow: 0 6px 16px rgba(15, 30, 50, 0.12);
}

.bs-kpi-card--value {
  background: linear-gradient(135deg, #052f5e 0%, #0a5a90 100%);
}
.bs-kpi-card--tax {
  background: linear-gradient(135deg, #7a1f1f 0%, #b23a3a 100%);
}
.bs-kpi-card--total {
  background: linear-gradient(135deg, #0d8478 0%, #15a355 100%);
}
.bs-kpi-card--warn {
  background: linear-gradient(135deg, #92400e 0%, #d97706 100%);
}

.bs-kpi-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

.bs-kpi-value {
  font-size: 20px;
  font-weight: 700;
  margin-top: 6px;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
}

.bs-section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 10px;
}

.bs-table {
  border: 1px solid #eef1f5;
  border-radius: 0;
  margin-bottom: 16px;
}

.bs-total-row {
  font-weight: 700;
  background: #f8fafc;
}

.bs-empty-cell {
  text-align: center;
  color: #94a3b8;
  padding: 16px 0 !important;
}

.bs-empty-state {
  text-align: center;
  padding: 48px 0;
  color: #94a3b8;
  font-size: 14px;
}

.v-menu__content {
  z-index: 9999 !important;
}
</style>
